import { Checkbox } from 'designSystem/components/Checkbox'
import {
  Badge,
  Callout,
  type CalloutTone,
  Hint,
  Panel,
  PanelTitle,
  Row,
} from 'features/sentinelDesign/designStyles'
import type { PlanAction } from 'features/sentinelDesign/planEdits'
import {
  planNoteRenderer,
  unscopedWarnings,
} from 'features/sentinelDesign/PlanRationale'
import type {
  SentinelDesignContext,
  SentinelDesignResult,
  SentinelPlan,
  SentinelPlanWarning,
} from 'features/sentinelDesign/types'
import { CleaningControls } from 'features/timeSeries/experiment/CleaningControls'
import { CollapsibleSection } from 'features/timeSeries/experiment/CollapsibleSection'
import { KNOB_HELP } from 'features/timeSeries/experiment/knobCopy'
import {
  KnobCheckRow,
  KnobField,
  KnobInput,
  KnobRow,
  KnobSelect,
} from 'features/timeSeries/experiment/knobFields'
import { KnobHelp } from 'features/timeSeries/experiment/KnobHelp'
import { ModelKnobs } from 'features/timeSeries/experiment/ModelKnobs'
import { SamplingKnobs } from 'features/timeSeries/experiment/SamplingKnobs'
import { SplitBuilder } from 'features/timeSeries/experiment/SplitBuilder'
import type { GateScope } from 'features/timeSeries/experiment/types'
import { Dispatch, FC } from 'react'
import styled from 'styled-components'

const Narrative = styled.p`
  margin: 0;
  font-size: 0.8rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.forecast.defaultText};
`

const DriverGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(16rem, 1fr));
  gap: 0.3rem 0.9rem;
`

const TagStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`

const warningTone = (warning: SentinelPlanWarning): CalloutTone =>
  warning.severity === 'error'
    ? 'error'
    : warning.severity === 'info'
      ? 'neutral'
      : 'warning'

export interface PlanEditorProps {
  design: SentinelDesignResult
  plan: SentinelPlan
  context: SentinelDesignContext
  dispatch: Dispatch<PlanAction>
}

/**
 * The designed plan, every field of it editable. The agent's narrative sits at
 * the top, its per-field rationale renders under the control that field maps to,
 * and the controls themselves are the Experiment Lab's — same knobs, same help
 * copy, so a plan reviewed here means the same thing as one built in the lab.
 *
 * Warnings are shown prominently and never gate the run.
 */
export const PlanEditor: FC<PlanEditorProps> = ({
  design,
  plan,
  context,
  dispatch,
}) => {
  const noteFor = planNoteRenderer(design.rationale, design.warnings)
  const topWarnings = unscopedWarnings(design.warnings)
  const planTags = [plan.target, ...plan.drivers].filter(Boolean)
  const contextTags = context.tags

  return (
    <Panel aria-label='Model plan'>
      <Row>
        <PanelTitle>Model plan</PanelTitle>
        <Badge>{plan.drivers.length} drivers</Badge>
        <Badge>{plan.methods.length} methods</Badge>
        <Badge>revert-safe: every field below is editable</Badge>
      </Row>

      {design.narrative && <Narrative>{design.narrative}</Narrative>}

      {topWarnings.map((warning) => (
        <Callout
          key={`${warning.code ?? ''}${warning.message}`}
          $tone={warningTone(warning)}>
          {warning.code ? <strong>{warning.code}: </strong> : null}
          {warning.message}
        </Callout>
      ))}
      {design.warnings?.length ? (
        <Hint>
          Warnings are advisory — none of them stop a run. Fields they name are
          flagged in place below.
        </Hint>
      ) : null}

      <CollapsibleSection
        sectionKey='sentinel-plan-tags'
        title='Target and drivers'>
        <KnobField>
          <KnobHelp label='Target' help='The signal the model predicts.' />
          <KnobSelect
            value={plan.target}
            onChange={(e) =>
              dispatch({ type: 'setTarget', tag: e.target.value })
            }>
            <option value=''>Select a target…</option>
            {contextTags.map((tag) => (
              <option key={tag.tag} value={tag.tag}>
                {tag.tag}
                {tag.role_hint ? ` — ${tag.role_hint}` : ''}
              </option>
            ))}
          </KnobSelect>
          {noteFor('target')}
        </KnobField>

        <KnobField as='div'>
          <KnobHelp
            label='Drivers'
            help='The signals the model predicts the target from. The target itself can never also be a driver.'
          />
          <DriverGrid>
            {contextTags
              .filter((tag) => tag.tag !== plan.target)
              .map((tag) => (
                <KnobCheckRow key={tag.tag}>
                  <Checkbox
                    checked={plan.drivers.includes(tag.tag)}
                    onCheckedChange={() =>
                      dispatch({ type: 'toggleDriver', tag: tag.tag })
                    }
                  />
                  <span>
                    {tag.tag}
                    {tag.required ? ' (required)' : ''}
                  </span>
                </KnobCheckRow>
              ))}
          </DriverGrid>
          {contextTags.length <= 1 && (
            <Hint>The bundle carries no other tag to drive from.</Hint>
          )}
          {noteFor('drivers')}
        </KnobField>

        <KnobRow>
          <KnobField>
            <KnobHelp label='Start' help='First day of the training window.' />
            <KnobInput
              type='date'
              value={plan.start_date}
              max={plan.end_date}
              onChange={(e) =>
                dispatch({
                  type: 'patch',
                  patch: { start_date: e.target.value },
                })
              }
            />
            {noteFor('start_date')}
          </KnobField>
          <KnobField>
            <KnobHelp label='End' help='Last day of the training window.' />
            <KnobInput
              type='date'
              value={plan.end_date}
              min={plan.start_date}
              onChange={(e) =>
                dispatch({ type: 'patch', patch: { end_date: e.target.value } })
              }
            />
            {noteFor('end_date')}
          </KnobField>
        </KnobRow>
        <Hint>
          The bundle was built over {context.window.start.slice(0, 10)} →{' '}
          {context.window.end.slice(0, 10)}. Widening the plan window past that
          trains on rows the plots above never showed you.
        </Hint>
      </CollapsibleSection>

      <CollapsibleSection
        sectionKey='sentinel-plan-sampling'
        title='Sampling and interval'>
        <SamplingKnobs
          config={plan}
          onPatch={(patch) => dispatch({ type: 'patch', patch })}
          noteFor={noteFor}
        />
      </CollapsibleSection>

      <CollapsibleSection
        sectionKey='sentinel-plan-split'
        title={<KnobHelp label='Split' help={KNOB_HELP.splitBuilder} />}>
        <SplitBuilder
          split={plan.split}
          chunks={[]}
          onChange={(split) => dispatch({ type: 'setSplit', split })}
        />
        {noteFor('split')}
      </CollapsibleSection>

      <CollapsibleSection sectionKey='sentinel-plan-knobs' title='Model knobs'>
        <ModelKnobs
          config={plan}
          onPatch={(patch) => dispatch({ type: 'patch', patch })}
          onFeaturePatch={(patch) => dispatch({ type: 'setFeature', patch })}
          onToggleMethod={(method) =>
            dispatch({ type: 'toggleMethod', method })
          }
          noteFor={noteFor}
        />
      </CollapsibleSection>

      <CollapsibleSection
        sectionKey='sentinel-plan-cleaning'
        title={`Cleaning (${planTags.length} tags)`}
        defaultOpen={false}>
        <KnobField>
          <KnobHelp label='Gate scope' help={KNOB_HELP.gateScope} />
          <KnobSelect
            value={plan.gate_scope}
            onChange={(e) =>
              dispatch({
                type: 'patch',
                patch: { gate_scope: e.target.value as GateScope },
              })
            }>
            <option value='all'>all rows</option>
            <option value='train_only'>train rows only</option>
          </KnobSelect>
          {noteFor('gate_scope')}
        </KnobField>
        {plan.cleaning_plan.summary && (
          <Hint>{plan.cleaning_plan.summary}</Hint>
        )}
        {noteFor('cleaning_plan')}
        <TagStack>
          {planTags.length === 0 && (
            <Hint>Pick a target and drivers to clean them.</Hint>
          )}
          {planTags.map((tag) => (
            <CleaningControls
              key={tag}
              tag={tag}
              isTarget={tag === plan.target}
              tagStats={contextTags.find((entry) => entry.tag === tag)?.stats}
              cleaningPlan={plan.cleaning_plan}
              onCleaningPlanChange={(cleaningPlan) =>
                dispatch({ type: 'setCleaning', cleaningPlan })
              }
            />
          ))}
        </TagStack>
      </CollapsibleSection>

      <CollapsibleSection
        sectionKey='sentinel-plan-phase'
        title='Phase shifts'
        defaultOpen={false}>
        <Hint>
          Periods to shift a driver by before it is aligned to the target —
          positive lags the driver, negative leads it.
        </Hint>
        {plan.drivers.length === 0 && <Hint>No drivers to shift.</Hint>}
        {plan.drivers.map((driver) => (
          <KnobField key={driver}>
            <span>{driver}</span>
            <KnobInput
              type='number'
              value={plan.phase_shifts[driver] ?? 0}
              onChange={(e) =>
                dispatch({
                  type: 'setPhaseShift',
                  tag: driver,
                  periods: Number(e.target.value) || 0,
                })
              }
            />
          </KnobField>
        ))}
        {noteFor('phase_shifts')}
      </CollapsibleSection>
    </Panel>
  )
}
